import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Hash, X, Ruler } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('veloflux_token')}` };
}

// ============== MODELS MANAGEMENT (Simple version - revision period is managed in Models page) ==============
export function ModelsManager({ open, onClose, onRefresh }) {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState('');
  const [newNumero, setNewNumero] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editNumero, setEditNumero] = useState('');

  const fetchModels = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/catalog/models`, { headers: getHeaders() });
      setModels(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { if (open) fetchModels(); }, [open]);

  const fetchNextNumero = async () => {
    try {
      const res = await axios.get(`${API}/catalog/models/next-numero`, { headers: getHeaders() });
      setNewNumero(String(res.data.next_numero));
    } catch { /* ignore */ }
  };

  useEffect(() => { if (open) fetchNextNumero(); }, [open]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim() || !newNumero) return;
    try {
      await axios.post(`${API}/catalog/models`, {
        numero_modelo: parseInt(newNumero),
        nombre_modelo: newName.trim()
      }, { headers: getHeaders() });
      toast.success('Modelo creado');
      setNewName(''); 
      fetchNextNumero();
      fetchModels();
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear modelo');
    }
  };

  const handleUpdate = async (id) => {
    try {
      await axios.put(`${API}/catalog/models/${id}`, {
        nombre_modelo: editName.trim(),
        numero_modelo: parseInt(editNumero)
      }, { headers: getHeaders() });
      toast.success('Modelo actualizado');
      setEditingId(null);
      fetchModels();
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`¿Eliminar modelo "${name}"?`)) return;
    try {
      await axios.delete(`${API}/catalog/models/${id}`, { headers: getHeaders() });
      toast.success('Modelo eliminado');
      fetchModels();
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col" data-testid="models-manager-modal">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Hash className="w-5 h-5 text-orange-500" />
            Gestionar Modelos
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 flex-1 min-h-0">
          {/* Create form */}
          <form onSubmit={handleCreate} className="flex gap-2 items-end flex-shrink-0">
            <div className="w-16">
              <Label className="text-xs">Nº</Label>
              <Input
                type="number" min="1" value={newNumero}
                onChange={(e) => setNewNumero(e.target.value)}
                placeholder="Nº" required data-testid="new-model-numero"
              />
            </div>
            <div className="flex-1">
              <Label className="text-xs">Nombre del modelo</Label>
              <Input
                value={newName} onChange={(e) => setNewName(e.target.value)}
                placeholder="Ej: Orbea Orca Aero" required data-testid="new-model-name"
              />
            </div>
            <Button type="submit" size="sm" className="bg-orange-500 hover:bg-orange-600" data-testid="create-model-btn">
              <Plus className="w-4 h-4" />
            </Button>
          </form>

          <p className="text-xs text-slate-500 bg-slate-50 p-2 rounded">
            El período de revisión se configura en la página "Modelos" del menú lateral.
          </p>

          {/* Models list - scrollable area */}
          <ScrollArea className="flex-1 min-h-0 border rounded-lg">
            {loading ? (
              <div className="p-4 text-center text-slate-400">Cargando...</div>
            ) : models.length === 0 ? (
              <div className="p-4 text-center text-slate-400">No hay modelos</div>
            ) : (
              <div className="divide-y">
                {models.map(m => (
                  <div key={m.id} className="flex items-center gap-2 p-3 hover:bg-slate-50" data-testid={`model-row-${m.id}`}>
                    {editingId === m.id ? (
                      <>
                        <Input type="number" min="1" className="w-14" value={editNumero}
                          onChange={(e) => setEditNumero(e.target.value)} />
                        <Input className="flex-1" value={editName}
                          onChange={(e) => setEditName(e.target.value)} />
                        <Button size="sm" variant="ghost" onClick={() => handleUpdate(m.id)}>
                          Guardar
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Badge variant="outline" className="text-orange-700 bg-orange-50 font-mono">
                          {m.numero_modelo}
                        </Badge>
                        <span className="flex-1 text-sm font-medium">{m.nombre_modelo}</span>
                        <Button size="sm" variant="ghost" onClick={() => {
                          setEditingId(m.id); 
                          setEditName(m.nombre_modelo); 
                          setEditNumero(String(m.numero_modelo));
                        }} data-testid={`edit-model-${m.id}`}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700"
                          onClick={() => handleDelete(m.id, m.nombre_modelo)} data-testid={`delete-model-${m.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        <DialogFooter className="flex-shrink-0">
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ============== TALLAS MANAGEMENT ==============
export function TallasManager({ open, onClose, onRefresh }) {
  const [tallas, setTallas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newTalla, setNewTalla] = useState('');

  const fetchTallas = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/catalog/tallas`, { headers: getHeaders() });
      setTallas(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { if (open) fetchTallas(); }, [open]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newTalla.trim()) return;
    try {
      await axios.post(`${API}/catalog/tallas`, { talla: newTalla.trim() }, { headers: getHeaders() });
      toast.success('Talla creada');
      setNewTalla('');
      fetchTallas();
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear talla');
    }
  };

  const handleDelete = async (id, talla) => {
    if (!window.confirm(`¿Eliminar talla "${talla}"?`)) return;
    try {
      await axios.delete(`${API}/catalog/tallas/${id}`, { headers: getHeaders() });
      toast.success('Talla eliminada');
      fetchTallas();
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col" data-testid="tallas-manager-modal">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Ruler className="w-5 h-5 text-orange-500" />
            Gestionar Tallas
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 flex-1 min-h-0">
          {/* Create form */}
          <form onSubmit={handleCreate} className="flex gap-2 items-end flex-shrink-0">
            <div className="flex-1">
              <Label className="text-xs">Nueva talla</Label>
              <Input
                value={newTalla} onChange={(e) => setNewTalla(e.target.value)}
                placeholder="Ej: 54, M, XL" required data-testid="new-talla-input"
              />
            </div>
            <Button type="submit" size="sm" className="bg-orange-500 hover:bg-orange-600" data-testid="create-talla-btn">
              <Plus className="w-4 h-4" />
            </Button>
          </form>

          <p className="text-xs text-slate-500 bg-slate-50 p-2 rounded">
            Para más opciones, visita la página "Tallas" del menú lateral.
          </p>

          {/* Tallas list - scrollable area */}
          <ScrollArea className="flex-1 min-h-0 border rounded-lg">
            {loading ? (
              <div className="p-4 text-center text-slate-400">Cargando...</div>
            ) : tallas.length === 0 ? (
              <div className="p-4 text-center text-slate-400">No hay tallas</div>
            ) : (
              <div className="divide-y">
                {tallas.map(t => (
                  <div key={t.id} className="flex items-center gap-2 p-3 hover:bg-slate-50" data-testid={`talla-row-${t.id}`}>
                    <Badge variant="outline" className="text-orange-700 bg-orange-50 font-mono">
                      {t.talla}
                    </Badge>
                    <span className="flex-1" />
                    <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700"
                      onClick={() => handleDelete(t.id, t.talla)} data-testid={`delete-talla-${t.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        <DialogFooter className="flex-shrink-0">
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
